import { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';

// Generic data item interface
interface DataItem {
  id: string;
  condoId?: string;
  unit?: string;
  userId?: string;
  createdBy?: string;
  assignedTo?: string;
  [key: string]: any;
}

/**
 * Hook to filter data by condo and user permissions
 * Ensures users only see data they're authorized to see
 */
export function useDataFilter<T extends DataItem>(data: T[] | undefined) {
  const { user, isSuperAdmin, isAdmin } = useAuth();

  const filteredData = useMemo(() => {
    if (!data || !user) return [];

    // Super admin sees everything
    if (isSuperAdmin()) {
      return data;
    }

    // Admin sees data from their assigned condos
    if (isAdmin()) {
      const assignedCondos = user.assignedCondos || [user.condoId];
      return data.filter(item => 
        assignedCondos.includes(item.condoId || '') || 
        item.condoId === user.condoId
      );
    }

    // Resident/Directive sees only their own data
    return data.filter(item => {
      // Check condo match
      if (item.condoId && item.condoId !== user.condoId) {
        return false;
      }
      
      // Check unit match (for unit-specific data)
      if (item.unit && item.unit !== user.unit) {
        return false;
      }
      
      // Check ownership (for personal data like tasks, complaints)
      if (item.userId && item.userId !== user.id) {
        return false;
      }
      
      // Check if created by this user
      if (item.createdBy && item.createdBy !== user.id) {
        return false;
      }
      
      // Check if assigned to this user
      if (item.assignedTo && item.assignedTo !== user.id) {
        return false;
      }
      
      return true;
    });
  }, [data, user, isSuperAdmin, isAdmin]);

  return filteredData;
}

/**
 * Hook to check if user can edit/delete an item
 */
export function useCanModify(item: DataItem | null | undefined) {
  const { user, isSuperAdmin, isAdmin } = useAuth();

  if (!item || !user) return false;

  // Super admin can modify anything
  if (isSuperAdmin()) return true;

  // Admin can modify items in their condos
  if (isAdmin()) {
    const assignedCondos = user.assignedCondos || [user.condoId];
    return assignedCondos.includes(item.condoId || '');
  }

  // Regular users can only modify their own items
  return (
    item.userId === user.id ||
    item.createdBy === user.id ||
    item.assignedTo === user.id
  );
}

/**
 * Hook to check if user can view an item
 */
export function useCanView(item: DataItem | null | undefined) {
  const { user, isSuperAdmin, isAdmin } = useAuth();

  if (!item || !user) return false;

  // Super admin can view everything
  if (isSuperAdmin()) return true;

  // Admin can view items in their assigned condos
  if (isAdmin()) {
    const assignedCondos = user.assignedCondos || [user.condoId];
    return assignedCondos.includes(item.condoId || '');
  }

  // Check condo access
  if (item.condoId && item.condoId !== user.condoId) {
    return false;
  }

  // Check unit access
  if (item.unit && item.unit !== user.unit) {
    return false;
  }

  // For personal items, check ownership
  if (item.userId || item.createdBy) {
    return item.userId === user.id || item.createdBy === user.id;
  }

  return true;
}

/**
 * Hook to enrich new data items with ownership info
 */
export function useEnrichData<T extends Partial<DataItem>>(data: T) {
  const { user } = useAuth();

  return useMemo(() => ({
    ...data,
    condoId: user?.condoId,
    userId: user?.id,
    unit: user?.unit,
    createdBy: user?.id,
  }), [data, user]);
}
